import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { hasManualLanguageChoice, markManualLanguage } from '@/lib/languageDetection';


const SUPPORTED_LANGUAGES = ['pt', 'en', 'fr', 'zh'] as const;
const normalizeLanguage = (lng?: string | null) => {
  const base = (lng || '').split('-')[0];
  return SUPPORTED_LANGUAGES.includes(base as typeof SUPPORTED_LANGUAGES[number]) ? base : undefined;
};

export function useLanguagePersistence() {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  useEffect(() => {
    // Se não há user autenticado, não sincroniza com BD (apenas usa cookie/localstorage)
    if (!user) return;

    let isMounted = true;

    // Buscar o idioma salvo no perfil
    const fetchUserLanguage = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error) throw error;

        // A escolha manual local tem prioridade; caso contrário aplica a preferência do perfil
        // (escolha manual feita noutro dispositivo — web, Android ou iOS).
        const profile = data as any;
        const profileLanguage = normalizeLanguage(profile?.preferred_language);
        if (!hasManualLanguageChoice() && profileLanguage && profileLanguage !== normalizeLanguage(i18n.language)) {
          if (isMounted) {
            markManualLanguage(profileLanguage);
            i18n.changeLanguage(profileLanguage);
          }
        }

      } catch (err) {
        console.error('[i18n] Error fetching preferred language from DB:', err);
      }
    };

    fetchUserLanguage();

    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    // Quando o utilizador mudar o idioma manualmente na interface, atualizar a DB
    const handleLanguageChange = async (lng: string) => {
      if (!user) return;
      const normalized = normalizeLanguage(lng) || 'pt';

      try {
        const { error } = await supabase
          .from('profiles')
          .update({ preferred_language: normalized } as any)
          .eq('id', user.id);

        if (error) throw error;
      } catch (err) {
        console.error('[i18n] Error updating preferred language to DB:', err);
      }
    };

    // Subscrição do evento do i18n
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [user, i18n]);
}
